if (!customElements.get('ns-routine-reveal')) {
  customElements.define('ns-routine-reveal', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.text = this.querySelector('[data-ns-routine-text]');
      this.number = this.querySelector('[data-ns-ring-number]');
      this.fill = this.querySelector('.ns-routine-ring-fill');
      this.target = Number(this.dataset.percent);
      if (!this.text || !this.number || !this.fill || !Number.isFinite(this.target)) return;
      this.target = Math.max(0, Math.min(100, this.target));
      this.countTarget = this.dataset.count != null ? Number(this.dataset.count) : this.target;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (this.motion.matches || !('IntersectionObserver' in window)) return;
      this.originalText = this.text.textContent;
      this.characters = [];
      const fragment = document.createDocumentFragment();
      for (const part of this.originalText.split(/(\s+)/)) {
        if (/^\s+$/.test(part)) {
          fragment.append(document.createTextNode(part));
          continue;
        }
        const word = document.createElement('span');
        word.className = 'ns-routine-word';
        for (const character of Array.from(part)) {
          const letter = document.createElement('span');
          letter.className = 'ns-routine-letter';
          letter.textContent = character;
          this.characters.push(letter);
          word.append(letter);
        }
        fragment.append(word);
      }
      this.text.replaceChildren(fragment);
      this.duration = Math.max(1800, Math.min(2300, this.characters.length * 32));
      this.elapsed = 0;
      this.revealed = 0;
      this.complete = false;
      this.started = false;
      this.inView = false;
      this.number.textContent = '0';
      this.fill.style.strokeDashoffset = '100';
      this.dataset.revealState = 'waiting';
      this.tick = this.tick.bind(this);
      this.onVisibility = () => this.syncPlayback();
      this.onMotion = (event) => { if (event.matches) this.finish(); };
      document.addEventListener('visibilitychange', this.onVisibility);
      this.motion.addEventListener('change', this.onMotion);
      this.observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        this.inView = entry.isIntersecting;
        if (!this.started && entry.intersectionRatio >= 0.6) this.started = true;
        this.syncPlayback();
      }, { threshold: [0, 0.6], rootMargin: '0px 0px -12% 0px' });
      this.observer.observe(this);
    }

    syncPlayback() {
      if (this.complete || !this.started) return;
      if (!this.inView || document.hidden) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
        this.lastTime = null;
        this.dataset.revealState = 'paused';
        return;
      }
      if (this.frame) return;
      this.dataset.revealState = 'playing';
      this.frame = requestAnimationFrame(this.tick);
    }

    tick(time) {
      this.frame = null;
      if (this.complete || !this.isConnected) return;
      if (!this.inView || document.hidden) {
        this.lastTime = null;
        this.dataset.revealState = 'paused';
        return;
      }
      if (this.lastTime != null) this.elapsed += Math.min(80, time - this.lastTime);
      this.lastTime = time;
      const progress = Math.min(1, this.elapsed / this.duration);
      const ringProgress = 1 - Math.pow(1 - progress, 3);
      this.fill.style.strokeDashoffset = String(100 - this.target * ringProgress);
      this.number.textContent = String(Math.round(this.countTarget * ringProgress));
      const count = Math.ceil(this.characters.length * Math.min(1, progress / 0.88));
      for (; this.revealed < count; this.revealed += 1) {
        this.characters[this.revealed].classList.add('is-visible');
      }
      if (progress >= 1) this.finish();
      else this.frame = requestAnimationFrame(this.tick);
    }

    finish() {
      this.complete = true;
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.lastTime = null;
      this.text.textContent = this.originalText;
      this.number.textContent = String(this.countTarget);
      this.fill.style.strokeDashoffset = String(100 - this.target);
      this.dataset.revealState = 'complete';
      this.removeListeners();
    }

    removeListeners() {
      this.observer?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.motion?.removeEventListener('change', this.onMotion);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.removeListeners();
      if (this.originalText != null) {
        this.text.textContent = this.originalText;
        this.number.textContent = String(this.countTarget);
        this.fill.style.strokeDashoffset = String(100 - this.target);
        delete this.dataset.revealState;
      }
      this.initialized = false;
      this.frame = null;
      this.lastTime = null;
    }
  });
}
